import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

const SESSION_PARAM = 'session';
const SESSION_VALUE = 'recording';
const LANGUAGE_PARAM = 'language';

type UseSpeakSessionDeepLinkParams = {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  selectedLanguage: string;
  sessionStarted: boolean;
  isStartingSession: boolean;
  /** Spec 004: no session, linked or not, starts before its place is known. */
  isPlaceReady: boolean;
  startSession: () => Promise<void>;
};

export function useSpeakSessionDeepLink({
  searchParams,
  setSearchParams,
  selectedLanguage,
  sessionStarted,
  isStartingSession,
  isPlaceReady,
  startSession,
}: UseSpeakSessionDeepLinkParams) {
  const sessionResumeActive = searchParams.get(SESSION_PARAM) === SESSION_VALUE;
  const startSessionRef = useRef(startSession);
  const autoStartedKeyRef = useRef<string | null>(null);
  const manualStartInFlightRef = useRef(false);

  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  const sessionReturnSearch = useMemo(() => {
    if (!selectedLanguage) {
      return `?${SESSION_PARAM}=${SESSION_VALUE}`;
    }

    const next = new URLSearchParams(searchParams);
    next.set(SESSION_PARAM, SESSION_VALUE);
    next.set(LANGUAGE_PARAM, selectedLanguage);
    return `?${next.toString()}`;
  }, [searchParams, selectedLanguage]);

  const markSessionInUrl = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set(SESSION_PARAM, SESSION_VALUE);
    if (selectedLanguage) {
      next.set(LANGUAGE_PARAM, selectedLanguage);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, selectedLanguage, setSearchParams]);

  const startLinkedSession = useCallback(async () => {
    if (manualStartInFlightRef.current || sessionStarted || isStartingSession || !isPlaceReady) return;

    manualStartInFlightRef.current = true;
    try {
      markSessionInUrl();
      await startSessionRef.current();
    } finally {
      manualStartInFlightRef.current = false;
    }
  }, [isPlaceReady, isStartingSession, markSessionInUrl, sessionStarted]);

  useEffect(() => {
    if (!sessionResumeActive) {
      autoStartedKeyRef.current = null;
      return;
    }

    if (!selectedLanguage || sessionStarted || isStartingSession || !isPlaceReady) return;

    const autoStartKey = `${SESSION_VALUE}:${selectedLanguage}`;
    if (autoStartedKeyRef.current === autoStartKey) return;

    autoStartedKeyRef.current = autoStartKey;
    void startSessionRef.current();
  }, [isPlaceReady, isStartingSession, selectedLanguage, sessionResumeActive, sessionStarted]);

  return {
    sessionResumeActive,
    sessionReturnSearch,
    startLinkedSession,
  };
}
